import { Component, EventEmitter, Input, Output, OnInit, Optional} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { AutoCompleteModule } from 'primeng/autocomplete'; // For prefix and phone type autocomplete
import { TagModule } from 'primeng/tag';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { AjaxService } from '../../../../../services/ajax.service';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

import { LOT_PHONE_PREFS, LOT_PHONE_TYPES } from '../../../../../services/account-api.constants';

// Interface for a phone number entry
export interface PhoneNumber {
    id: string;   // RecID
    recId: string; // PhoneRecId
    prefixId: string | null;
    prefix: string | null;
    phoneType: string | null;
    phoneTypeId: number | null;
    isActive: number;
    isApproved: number;
    number: string; // The phone without prefix.
    description: string;
}

// Interface for dropdown options
interface SelectItem {
    label: string;
    value: string;
}

@Component({
    selector: 'app-phone-numbers',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        InputTextarea,
        ButtonModule,
        ToastModule,
        TagModule,
        TableModule,
        AutoCompleteModule
    ],
    templateUrl: './phone-numbers.component.html',
    styleUrls: ['./phone-numbers.component.scss'],
    providers: [MessageService]
})
export class PhoneNumbersComponent implements OnInit {
    @Input() phoneNumbers: PhoneNumber[] = [];
    @Output() phoneNumbersChange = new EventEmitter<PhoneNumber[]>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();
	@Output() skipToLastStep = new EventEmitter<void>();


    @Input() customerRecId!: number;

    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;

    newEntry: PhoneNumber = {
        id: '',
        recId : '',
        prefixId : '',
        phoneType : '',
        phoneTypeId :0,
        isActive : 1,
        isApproved : 1,
        prefix: null,
        number: '',
        description: ''
    };

    // Phone number prefixes
    phoneNumberPrefixOptions: SelectItem[] = [];
    filteredPrefixes: SelectItem[] = [];
    
    // Phone types
    phoneTypeOptions: SelectItem[] = [];
    filteredPhoneTypes: SelectItem[] = [];

    constructor(private messageService: MessageService,
                public translateServ :TranslateService,
                public dirServ : DirectionService,
                private ajaxServ: AjaxService,
                private viewScopeService: ViewScopeService,
                @Optional() public ref: DynamicDialogRef,
                @Optional() public config: DynamicDialogConfig,
                @Optional() private maintainAccountService: MaintainAccountService
            ) {}

    ngOnInit(): void 
    {
        this.loadPrefixes();
        this.loadPhoneTypes(); // Load phone types on init
        if (this.config && this.config.data)
        {
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry) 
            {
                // Edit mode: initialize with existing record(s)
                this.phoneNumbers = [this.config.data.nameEntry];
                console.log('Current Phone Numbers Data:', this.phoneNumbers);
            } 
            else 
            {
                // Create mode: do NOT pre-push a row here.
                // The footer row with newEntry will handle input.
                this.phoneNumbers = [];
            }
        }
        else
        {
            this.isDialogMode = false;
            this.isCreate = true;
            
            // Ensure all existing items have a unique ID for tracking.
            this.phoneNumbers.forEach(item => {
                if (!item.id) {
                    item.id = this.generateUniqueId();
                }
            });
            this.resetNewEntry();
        }
    }

    private generateUniqueId(): string {
        return 'id_' + Math.random().toString(36).substring(2, 9);
    }

    private loadPrefixes() 
    {
        const url = this.ajaxServ.getBaseOfApi() + LOT_PHONE_PREFS + this.viewScopeService.getCurrentOpertaingUserId().toString();

        this.ajaxServ.getData(url).subscribe((response: any) => {
            const data = response as any[];
            this.phoneNumberPrefixOptions = data.map(item => ({
            label: item.Pref,
            value: String(item.PhonePrefRecID)
            }));
            this.bootstrapExistingSelections();
        });
    }
    
    private loadPhoneTypes()
    {
        const url = this.ajaxServ.getBaseOfApi() + LOT_PHONE_TYPES + this.viewScopeService.getCurrentOpertaingUserId().toString();
        this.ajaxServ.getData(url).subscribe((response: any) => {
            const data = response as any[];
            this.phoneTypeOptions = data.map(item => ({
                label: item.PhoneType, // Assuming 'Type' is the display field
                value: String(item.PhoneTypeRecID) // Assuming 'PhoneTypeRecID' is the value field
            }));
            this.bootstrapExistingSelections();
        });
    }

    private bootstrapExistingSelections(): void {
        if (!this.phoneNumbers || !this.phoneNumberPrefixOptions || !this.phoneTypeOptions) return;

        this.phoneNumbers.forEach(entry => {
            // Match Prefix
            let matchedPrefix = this.phoneNumberPrefixOptions.find(o => o.value === String(entry.prefixId));
            if (!matchedPrefix && entry.prefix) {
                matchedPrefix = this.phoneNumberPrefixOptions.find(o => o.label === entry.prefix);
            }
            if (matchedPrefix) {
                entry.prefix = matchedPrefix.label;
                entry.prefixId = matchedPrefix.value;
            }
            
            // Match Phone Type
            let matchedType = this.phoneTypeOptions.find(o => o.value === String(entry.phoneTypeId));
            if (!matchedType && entry.phoneType) {
                matchedType = this.phoneTypeOptions.find(o => o.label === entry.phoneType);
            }
            if (matchedType) {
                entry.phoneType = matchedType.label;
                entry.phoneTypeId = Number(matchedType.value);
            }
        });
    }


    resetNewEntry(): void {
        this.newEntry = {
            id: this.generateUniqueId(),
            prefixId : '',
            prefix: null,
            recId : '',
            phoneType : null,
            phoneTypeId :0,
            isActive : 1,
            isApproved : 1,
            number: '',
            description: ''
        };
    }

    addEntry(): void {
        if (!this.newEntry.prefix || !this.newEntry.number.trim() || !this.newEntry.phoneType) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Prefix, Phone Number, and Phone Type are required for new entry.',
                life: 3000
            });
            return;
        }
        if (!/^\d+$/.test(this.newEntry.number.trim())) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Phone Number must contain only digits.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }


        this.phoneNumbers.push({ ...this.newEntry });
        this.emitChanges();
        if(this.isCreate)
        {
            this.resetNewEntry();    
        }
        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New phone number added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: PhoneNumber): void {
        this.phoneNumbers = this.phoneNumbers.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Phone number removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.phoneNumbersChange.emit(this.phoneNumbers);
    }

    getRemainingChars(description: string | null): number {
        const currentDescription = description ?? '';
        return this.DESCRIPTION_MAX_LENGTH - currentDescription.length;
    }

    validateAndProceed(): void {
        let isValid = true;
        for (const entry of this.phoneNumbers) {
            if (!entry.prefix || !entry.number.trim() || !entry.phoneType) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Prefix, Phone Number, and Phone Type are required for all entries.`,
                    life: 3000
                });
                break;
            }
            if (!/^\d+$/.test(entry.number.trim())) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Phone Number "${entry.number}" must contain only digits.`,
                    life: 3000
                });
                break;
            }
            const description = entry.description || '';
            if (description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for phone number "${entry.prefix} ${entry.number}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    /**
     * Filters phone number prefixes for autocomplete.
     * @param event The autocomplete event containing the query.
     */
    searchPrefixes(event: any, entry: PhoneNumber): void 
    {
        const q = (event.query || '').toLowerCase();
        this.filteredPrefixes = this.phoneNumberPrefixOptions.filter(o =>
            o.label.toLowerCase().includes(q)
        );
    }
    
    /**
     * Filters phone types for autocomplete.
     * @param event The autocomplete event containing the query.
     */
    searchPhoneTypes(event: any, entry: PhoneNumber): void 
    {
        const q = (event.query || '').toLowerCase();
        this.filteredPhoneTypes = this.phoneTypeOptions.filter(o =>
            o.label.toLowerCase().includes(q)
        );
    }

    applyRowChanges(entry: PhoneNumber): void {
        // Simply emit the changes so parent knows data is dirty
        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${entry.number}". Remember to press Save to persist.`,
            life: 2500
        });
    }

    /**
     * This function is intended for the dialog's "save" button.
     * It validates the form and then calls the API to save/update the data.
     * It uses forkJoin to wait for all API calls to complete before closing the dialog.
     */
    saveAndSubmit(): void 
    {
        let isValid = true;
        for (const entry of this.phoneNumbers) {
            if (!entry.number.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Name for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if ((entry.description ?? '').length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.number}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        if (this.maintainAccountService) {
            const requests = this.phoneNumbers.map(entry => {
                const basePayload: any = {
                    PhonePrefRecID: Number(entry.prefixId),
                    Phone : entry.number,
                    PhoneTypeRecID : entry.phoneTypeId,
                    Descr: entry.description || '',
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
                };
                if (this.isCreate) {
                    basePayload.CustomerSupplierRecID = this.customerRecId;
                } else {
                    basePayload.PhoneRecID =entry.recId;
                    basePayload.RecID =   entry.id;
                }
                return basePayload;
            });

            console.log("Data we will save:", requests);
            requests.forEach(record => {
                this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1),JSON.stringify(record)).subscribe(
                {
                    next: (response) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: `Saved record for "${record.AdditName}".`,
                            life: 2000
                        });
                        if (this.ref && record === requests[requests.length - 1]) {
                            this.ref.close(response);
                        }
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: `Failed to save record for "${record.AdditName}".`,
                            life: 3000
                        });
                    },
                    complete: () => 
                    {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Complete',
                            detail: 'All records processed.',
                            life: 2000
                        });
                        if (this.ref) 
                        {
                            this.ref.close(true);
                        }
                    }
                });
            });
        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }
    
    onPhoneTypeSelected(event: any, entry: PhoneNumber): void 
    {
        const selectedOption = event.value as SelectItem;
        if (selectedOption) {
            entry.phoneTypeId = Number(selectedOption.value);
            entry.phoneType = selectedOption.label;
        }
        this.emitChanges();
    }


    onPrefixSelected(event: any, entry: PhoneNumber): void 
    {
        const selectedOption = event.value as SelectItem;
        if (selectedOption) {
            entry.prefixId = selectedOption.value;
            entry.prefix   = selectedOption.label;
        }
        this.emitChanges();
    }

    getSeverity(val: boolean): string 
    {
        return val ? 'success' : 'danger';
    }

    toggleIsActive(entry: any): void 
    {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void 
    {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }


    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }
}
